/**
 * Refuses a paste or text drop that would push the editor past
 * `EDITOR_TEXT_LIMIT_CHARS`.
 *
 * Both run as capture-phase listeners on the editor's document, scoped to the
 * editor: a paste must target Monaco's own `textarea.inputarea` (pastes into
 * the find/replace widget, which also lives inside the container, never touch
 * the model), a drop may target anywhere inside the container. They must run
 * before ANY Monaco listener: once a 256M-character document exists every
 * later `getValue()` throws (see `monacoModelText.ts`), and Monaco's change
 * listeners fire synchronously during the insert — after the fact is too
 * late. The document level is the only place that is reliably first: Monaco
 * 0.44's `CopyPasteController` registers its own capture `paste` listener on
 * `getContainerDomNode()` at editor creation and calls
 * `stopImmediatePropagation()` there, so a listener on `getDomNode()` (the
 * `.monaco-editor` child) or a later listener on the container never sees
 * it; `drop` is handled by the view's mouse handler (which fires
 * `onDropIntoEditor`), and a document capture listener precedes that too.
 */

import {
  describeOversizedText,
  exceedsEditorTextLimit,
  sizeBucketForChars,
  type OversizedTextSource,
} from '../../../../types/editorTextLimits';

type SelectionLike = unknown;

// Method signatures (not function-typed properties) on purpose: they are
// checked bivariantly, so the real, fully typed Monaco editor/model (whose
// `getValueLengthInRange(range: IRange, eol?)` is narrower than `unknown`)
// satisfies these structural shapes without a cast.
type GuardedModel = {
  getValueLength(): number;
  getValueLengthInRange?(range: SelectionLike): number;
  getEOL?(): string;
};

type GuardedEditor = {
  /** Monaco's outer container (the element passed to `editor.create`). */
  getContainerDomNode?(): HTMLElement | null;
  /** The `.monaco-editor` element; fallback scope when the container is unavailable. */
  getDomNode(): HTMLElement | null;
  getModel(): GuardedModel | null;
  getSelections?(): SelectionLike[] | null;
};

export type OversizedInsertSource = Exclude<OversizedTextSource, 'file'>;

export type OversizedPasteDetails = {
  source: OversizedInsertSource;
  /** Characters the editor would have held after the insert. */
  totalChars: number;
  /** Low-cardinality bucket of the INSERTED text size for analytics. */
  sizeBucket: string;
  /** User-facing explanation (names the resulting tab size). */
  message: string;
};

export type InsertSizing = {
  existingChars: number;
  /** Characters the insert replaces (sum over all selections). */
  selectedChars: number;
  /** Cursors the text is inserted at; Monaco inserts the whole text at each one. */
  selectionCount: number;
  insertedChars: number;
};

/**
 * Pure decision, exported for tests. Resulting size = existing − replaced +
 * inserted × cursors. Monaco's multi-cursor paste can also distribute one
 * line per cursor when line count equals cursor count; counting the whole
 * text per cursor is the conservative bound and errs toward refusing.
 */
export function decideOversizedInsert(sizing: InsertSizing, source: OversizedInsertSource): OversizedPasteDetails | null {
  if (sizing.insertedChars <= 0) return null;
  const cursors = Math.max(1, sizing.selectionCount);
  const totalChars = Math.max(0, sizing.existingChars - sizing.selectedChars) + sizing.insertedChars * cursors;
  if (!exceedsEditorTextLimit(totalChars)) return null;
  return {
    source,
    totalChars,
    sizeBucket: sizeBucketForChars(sizing.insertedChars),
    message: describeOversizedText(totalChars, source),
  };
}

/** Back-compat shape used by earlier tests: single cursor, nothing replaced. */
export function decideOversizedPaste(existingChars: number, pastedChars: number): OversizedPasteDetails | null {
  return decideOversizedInsert({ existingChars, selectedChars: 0, selectionCount: 1, insertedChars: pastedChars }, 'paste');
}

/**
 * Characters Monaco will actually insert: it normalises every line break in
 * pasted text to the model's EOL, so `\n` in a CRLF document counts as two.
 */
export function normalisedInsertLength(text: string, eol: string | undefined): number {
  const eolLength = eol ? eol.length : 1;
  let length = 0;
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code === 13) {
      // "\r\n" is one break; a lone "\r" is one break too.
      if (text.charCodeAt(i + 1) === 10) i += 1;
      length += eolLength;
    } else if (code === 10) {
      length += eolLength;
    } else {
      length += 1;
    }
  }
  return length;
}

function measureEditor(editor: GuardedEditor, source: OversizedInsertSource): Omit<InsertSizing, 'insertedChars'> {
  const model = editor.getModel();
  if (!model) return { existingChars: 0, selectedChars: 0, selectionCount: 1 };
  // An external drop is ONE insertion at the drop point: Monaco collapses the
  // selection there and replaces nothing, so no selection maths applies.
  if (source === 'drop') return { existingChars: model.getValueLength(), selectedChars: 0, selectionCount: 1 };
  const selections = editor.getSelections?.() ?? [];
  let selectedChars = 0;
  if (typeof model.getValueLengthInRange === 'function') {
    for (const selection of selections) {
      try {
        selectedChars += model.getValueLengthInRange(selection);
      } catch {
        // A stale selection must not disable the guard; count it as empty.
      }
    }
  }
  return { existingChars: model.getValueLength(), selectedChars, selectionCount: Math.max(1, selections.length) };
}

function readTransferText(transfer: DataTransfer | null): string | null {
  if (!transfer) return null;
  try {
    return transfer.getData('text/plain');
  } catch {
    return null;
  }
}

/**
 * Install the guard. Returns a disposer; call it when the editor unmounts.
 * `onBlocked` receives the details so the caller can toast and track.
 */
export function installOversizedPasteGuard(
  editor: GuardedEditor,
  onBlocked: (details: OversizedPasteDetails) => void,
): () => void {
  const scope = editor.getContainerDomNode?.() ?? editor.getDomNode();
  if (!scope) return () => {};
  const doc = scope.ownerDocument;

  const guard = (event: Event, source: OversizedInsertSource, transfer: DataTransfer | null, targetOk: boolean) => {
    if (!targetOk) return;
    const inserted = readTransferText(transfer);
    if (inserted === null) return;
    const insertedChars = normalisedInsertLength(inserted, editor.getModel()?.getEOL?.());
    const details = decideOversizedInsert({ ...measureEditor(editor, source), insertedChars }, source);
    if (!details) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    onBlocked(details);
  };

  const isEditorTextarea = (target: EventTarget | null): boolean =>
    target instanceof HTMLTextAreaElement && target.classList.contains('inputarea') && scope.contains(target);
  const isInsideEditor = (target: EventTarget | null): boolean => target instanceof Node && scope.contains(target);

  const handlePaste = (event: Event) =>
    guard(event, 'paste', (event as ClipboardEvent).clipboardData, isEditorTextarea(event.target));
  const handleDrop = (event: Event) =>
    guard(event, 'drop', (event as DragEvent).dataTransfer, isInsideEditor(event.target));

  doc.addEventListener('paste', handlePaste, true);
  doc.addEventListener('drop', handleDrop, true);
  return () => {
    doc.removeEventListener('paste', handlePaste, true);
    doc.removeEventListener('drop', handleDrop, true);
  };
}
